import { useNavigate } from "@tanstack/react-router";
import { IconAlertTriangle, IconArrowLeft, IconClock } from "@tabler/icons-react";
import { Alert, Button, Group, PageHeader, Stack } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { FlujoCard } from "./FlujoCard";
import { ChatCard } from "./ChatCard";
import { AccionesAutorizadorCard } from "./AccionesAutorizadorCard";
import { ResumenSolicitudCard } from "./ResumenSolicitudCard";
import { PolizasCard } from "@/features/contabilizacion/components/PolizasCard";
import { ProrrateoResumenCard } from "@/features/solicitud-detalle/components/ProrrateoResumenCard";
import { DocumentosResumenCard } from "@/features/solicitud-detalle/components/DocumentosResumenCard";
import { RechazoBanner } from "@/features/solicitud-detalle/components/RechazoBanner";
import { useUserPermisos } from "@/lib/auth-store";
import { PERMISOS } from "@/lib/permisos";
import { enFlujoDeAutorizacion } from "../utils/estado-autorizacion";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { useSolicitudPolling } from "@/features/solicitudes/hooks/use-solicitud-polling";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/autorizacion";

/** Mapea la `key` del paso del stepper al id de la sección en esta pantalla. */
const ANCLA_POR_PASO: Record<string, string> = {
  crear: "sec-resumen",
  contrato: "sec-resumen",
  orden: "sec-resumen",
  cfdi: "sec-resumen",
  datos: "sec-resumen",
  proveedor: "sec-resumen",
  conciliacion: "sec-resumen",
  prorrateo: "sec-prorrateo",
  documentos: "sec-documentos",
  contabilizacion: "sec-contabilizacion",
  autorizacion: "sec-autorizacion",
};

/**
 * Pantalla del paso "Autorización" del flujo de la solicitud. El autorizador
 * (incluido Contabilidad para el subtipo Invoice) revisa aquí TODO el soporte
 * antes de decidir: resumen, prorrateo y documentos (read-only) + timeline del
 * flujo + chat + acciones. Los pasos del stepper hacen scroll a cada sección.
 * Polling cada 5s para reflejar autorizaciones en tiempo real.
 */
export function AutorizacionPasoPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: sol } = useSolicitud(id);
  useSolicitudPolling(id, 5_000);

  // El solicitante también puede tener rol Autorizador/MesaDeControl (típico
  // de coordinadores). Si lo tiene Y la solicitud está activa en el flujo de
  // autorización, mostramos las acciones del autorizador antes del chat para
  // que pueda resolver desde la misma pantalla.
  // Hotspot 90 · por PERMISO: quien puede autorizar o dar de baja ve la tarjeta de
  // acciones (dentro, autorizar/rechazar siguen dependiendo del rol del nivel actual).
  const permisos = useUserPermisos();
  const puedeResolver =
    permisos.includes(PERMISOS.autorizacionAutorizar) || permisos.includes(PERMISOS.autorizacionDarDeBaja);
  const mostrarAcciones = puedeResolver && enFlujoDeAutorizacion(sol.estado);

  // A dónde "regresa" quien está aquí: un autorizador viene de su bandeja; el resto (el
  // solicitante, Mesa de Control sin permiso de autorizar…) de la lista de solicitudes.
  const esAutorizador = permisos.includes(PERMISOS.autorizacionAutorizar);
  const volver = esAutorizador
    ? ({ to: "/autorizacion/bandeja", label: "Bandeja de autorización", boton: "Volver a la bandeja" } as const)
    : ({ to: "/solicitudes", label: "Solicitudes", boton: "Volver a solicitudes" } as const);

  // El stepper aquí NO navega a rutas de captura (es una pantalla de revisión):
  // al pinchar un paso hace scroll a la sección correspondiente del resumen.
  const scrollASeccion = (stepKey: string) => {
    const anclaId = ANCLA_POR_PASO[stepKey];
    if (!anclaId) return;
    document.getElementById(anclaId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title="Autorización"
        description={`Solicitud ${sol.folio} · ${sol.flujo?.cantidadNiveles ?? 0} nivel(es) de autorización`}
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: volver.label, href: volver.to },
          { label: sol.folio, href: `/solicitudes/${id}` },
          { label: "Autorización" },
        ]}
        actions={
          <Button variant="default" leftSection={<IconArrowLeft size={14} />} onClick={() => void navigate({ to: volver.to })}>
            {volver.boton}
          </Button>
        }
      />
      <LayoutConStepper
        estado={sol.estado}
        subtipo={sol.subtipo}
        tipo={sol.tipo}
        prorrateoAutomatico={sol.prorrateoAutomatico}
        paso="autorizacion"
        solicitudId={id}
        contabilizada={sol.enviadaAContabilizacion}
        fillHeight
        permiteNavegar={false}
        onStepSoloLectura={scrollASeccion}
      >
        <Stack gap="md">
          {/* Rechazada por un autorizador · `SolicitudDetallePage` redirige aquí a quien actúa
              sobre la solicitud, así que el banner con los motivos y las acciones de corrección
              ("Editar archivos" / "Reenviar al nivel N") debe vivir en esta pantalla. */}
          {sol.estado === "RechazadaParaCorreccion" && (
            <RechazoBanner
              solicitudId={id}
              solicitanteId={sol.solicitanteId}
              motivos={sol.ultimoMotivoRechazo}
              nivelDelRechazo={sol.flujo?.nivelActual ?? null}
            />
          )}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-8">
              <Stack gap="md">
                <div id="sec-resumen">
                  <ResumenSolicitudCard sol={sol} />
                </div>
                <ProrrateoResumenCard sol={sol} />
                <DocumentosResumenCard sol={sol} />
                <div id="sec-contabilizacion">
                  {sol.enviadaAContabilizacion ? (
                    <PolizasCard solicitudId={id} />
                  ) : (
                    <Alert variant="info" icon={<IconClock size={16} stroke={1.7} />}>
                      La póliza aún no se ha generado · la contabilización corre en paralelo al
                      flujo de autorización. Vuelve a revisar en unos segundos.
                    </Alert>
                  )}
                </div>
                <div id="sec-autorizacion">
                  {sol.flujo ? (
                    <FlujoCard flujo={sol.flujo} />
                  ) : (
                    <Alert variant="readonly" icon={<IconAlertTriangle size={16} />}>
                      La solicitud aún no tiene un flujo de autorización asignado.
                    </Alert>
                  )}
                </div>
                {mostrarAcciones ? <AccionesAutorizadorCard id={id} /> : null}
              </Stack>
            </div>
            <div className="col-span-12 md:col-span-4">
              <div className="sticky top-[calc(var(--shell-header-h)+16px)]">
                <ChatCard solicitudId={id} />
              </div>
            </div>
          </div>
          <Group justify="flex-start">
            <Button variant="default" leftSection={<IconArrowLeft size={14} />} onClick={() => void navigate({ to: volver.to })}>
              {volver.boton}
            </Button>
          </Group>
        </Stack>
      </LayoutConStepper>
    </div>
  );
}
